import React from "react";
import type { Dispatch } from "redux";

import * as tt from "@/lib/backend/ticketing.generated.types";
import UserStepField from "./UserStepField";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import SpanButton from "@/modules/shared/ui/SpanButton";
import TicketDetailsModal from "./TicketDetailsModal";
import { setTimeoutAsync } from "@/lib/util";

import { SET_INBOX_VIEW } from "./reducers/ticketing";
import type { ActionTypes } from "./reducers/ticketing";
import type { TicketInboxData } from "./commonTicketTypes";
import { FormattedMessage } from "react-intl";
import SimpleMarkdownRenderer from "@/modules/shared/ui/SimpleMarkdownRenderer";
import * as Auth from "@/lib/auth";

type Props = {
    ticket_uuid: string;
    reduxDispatch?: Dispatch<ActionTypes>;
    is_kiosk: boolean;
    close?: () => void;
    wait_for?: tt.IWaitForIndication | null;
    is_open?: boolean;
};

type State = {
    error: string;
    show_modal: boolean;
    field_values: tt.IFieldValuePair[];
    successfull_save: boolean;
    ticket: TicketInboxData;
    ticket_user_step: tt.IGetUserStepInfoRes;
    text_to_show: string;
    ticket_variables: tt.IVariableInfo[];
    ticket_attachments: tt.ITicketAttachment[];
};

const comment_translations = {
    material_check_comment: "Preverjanje materialov",
    preparation_comment: "Priprava orodja v izvajanju",
    start_setup_comment: "Čakanje na nastavljanje",
    setup_comment: "Zaključi menjavo orodja",
    start_preparation_comment: "Čakanje na pripravo",
};

const mapFieldValuesToState = (fields: tt.IUserStepField[]): tt.IFieldValuePair[] => {
    const field_values = fields.map(f => {
        return {
            name: f.name,
            value: f.def_value || "",
        };
    });
    return field_values;
};

class UserStep extends React.Component<Props, State> {
    state: State = {
        error: "",
        field_values: [],
        show_modal: false,
        successfull_save: false,
        ticket: {
            user_step: null,
            title: "",
            workflow_title: "",
            step: "",
        },
        text_to_show: "",
        ticket_user_step: {
            uuid: "",
            name: "",
            title: "",
            text: "",
            fields: [],
            button_title: "",
            has_prev_step: false,
            return_button_title: "",
        },
        ticket_variables: [],
        ticket_attachments: [],
    };

    async componentDidMount() {
        const username = Auth.getLoggedinUser().username;

        if (this.props.ticket_uuid !== undefined) {
            let ticket = await getBackend2().ticketing.getTicketBasicInfo({ uuid: this.props.ticket_uuid });

            if (
                ticket !== undefined &&
                this.props.wait_for !== undefined &&
                this.props.wait_for !== null &&
                this.props.wait_for.step_name !== undefined
            ) {
                const message_to_show = this.props.wait_for.message || "";
                this.setState({ text_to_show: message_to_show });

                let requests_already_sent = 0;
                let number_of_attempts = 0;

                if (this.props && this.props.wait_for && this.props.wait_for.timeout_msec) {
                    number_of_attempts = Math.floor(this.props.wait_for.timeout_msec / 2000);
                }

                while (requests_already_sent < number_of_attempts) {
                    if (this.props.is_open === false) {
                        break;
                    }

                    if (this.props.wait_for && this.props.wait_for.step_name === ticket.step) {
                        if (ticket.assigned_to === username) {
                            const ticket_user_step = await getBackend2().ticketing.getUserStepInfo({
                                uuid: this.props.ticket_uuid,
                            });

                            ticket.title = "";
                            ticket.workflow_title = "";

                            // Select first option on dropdowns and set it as default value
                            // This is a QoL improvement for non-group users
                            ticket.user_step = this.selectFirstOptionForDropdowns(ticket_user_step);
                            this.setState({
                                ticket: {
                                    user_step: ticket_user_step,
                                    title: ticket.title,
                                    workflow_title: ticket.workflow_title,
                                    step: ticket.step,
                                },
                                field_values: mapFieldValuesToState(ticket_user_step.fields),
                            });
                            break;
                        } else {
                            this.props.close && this.props.close();
                            break;
                        }
                    }

                    await setTimeoutAsync(2000);

                    ticket = await getBackend2().ticketing.getTicketBasicInfo({ uuid: this.props.ticket_uuid });
                    requests_already_sent++;
                }

                // timeout
                if (requests_already_sent === number_of_attempts) {
                    this.props.close && this.props.close();
                }
            } else {
                if (ticket.assigned_to === username) {
                    const ticket_user_step = await getBackend2().ticketing.getUserStepInfo({
                        uuid: this.props.ticket_uuid,
                    });
                    const ticket_variables = await getBackend2().ticketing.getTicketVariables({
                        uuid: this.props.ticket_uuid,
                    });
                    const ticket_attachments = await getBackend2().ticketing.getTicketAttachments({
                        uuid: this.props.ticket_uuid,
                    });

                    ticket.title = "";
                    ticket.workflow_title = "";

                    // Select first option on dropdowns and set it as default value
                    // This is a QoL improvement for non-group users
                    ticket.user_step = this.selectFirstOptionForDropdowns(ticket_user_step);

                    this.setState({
                        ticket: {
                            user_step: ticket_user_step,
                            title: ticket.title,
                            workflow_title: ticket.workflow_title,
                            step: ticket.step,
                        },
                        ticket_user_step: this.selectFirstOptionForDropdowns(ticket_user_step),
                        field_values: mapFieldValuesToState(ticket_user_step.fields),
                        ticket_variables: ticket_variables.variables,
                        ticket_attachments: ticket_attachments.attachments,
                    });
                }
            }
        }
    }

    selectFirstOptionForDropdowns(ticket_user_step: tt.IGetUserStepInfoRes): tt.IGetUserStepInfoRes {
        const fields = ticket_user_step.fields;
        for (const field of fields) {
            if (field.type === "dropdown" && field.items && field.items.length > 0) {
                const items = field.items;
                const findValue = items.find(item => item.value === field.def_value);

                // If the default value is not found, set the first item as default value
                // For cases where ticket was rejected and assigned to another user OR
                // Ticket is opened by a user who is not a part of a group
                if (findValue === undefined || field.def_value === "") {
                    field.def_value = items[0].value;
                }
            }
        }
        return ticket_user_step;
    }

    async onSave(dont_move_to_next_step?: boolean) {
        const ticket_uuid = this.props && this.props.ticket_uuid;
        let res: tt.ICloseUserStepRes | null = null;
        try {
            const ticket_user_fields = this.state.ticket.user_step?.fields ?? [];
            const dropdown_fields = ticket_user_fields.filter(f => f.type === "dropdown" && f.mandatory === true);

            // Check if there are any mandatory dropdown fields
            // If there are, check if they have default values
            // If they don't, show an error message
            for (const field of dropdown_fields) {
                if (field.def_value === "") {
                    this.setState({
                        error: translate("common.choose_option", "Choose an option"),
                    });
                    return;
                }
            }

            res = await getBackend2().ticketing.closeUserStep({
                uuid: ticket_uuid,
                field_values: this.state.field_values,
                dont_move_to_next_step: dont_move_to_next_step || false,
            });
        } catch (e) {
            this.setState({
                error: (e as Error).message,
            });
        }
        if (!res) {
            return;
        }
        if (res.success) {
            this.setState({
                successfull_save: true,
                error: "",
            });

            if (this.props.reduxDispatch !== undefined) {
                // switch to inbox view
                setTimeout(() => {
                    this.props.reduxDispatch && this.props.reduxDispatch({ type: SET_INBOX_VIEW, data: undefined });
                }, 2000);
            } else {
                if (this.props.close !== undefined) {
                    // wait 2 seconds and then close the modal
                    setTimeout(() => {
                        this.props.close && this.props.close();
                    }, 2000);
                }
            }
        }
    }

    async rejectAndMoveToPrevStep() {
        const comment: tt.IFieldValuePair | tt.IUserStepField | void = this.state.field_values.find(fv =>
            fv.name.includes("comment")
        );
        if (!comment || !comment.value) {
            this.setState({
                error: translate("Ticketing.enter_comment", "Please enter comment"),
            });
            return;
        }

        const ticket_user_fields = this.state.ticket.user_step?.fields ?? [];
        const dropdown_fields = ticket_user_fields.filter(f => f.type === "dropdown" && f.mandatory === true);

        // Check if there are any mandatory dropdown fields
        // If there are, check if they have default values
        // If they don't, show an error message
        for (const field of dropdown_fields) {
            if (field.def_value === "") {
                this.setState({
                    error: translate("Ticketing.choose_option", "Choose an option"),
                });
                return;
            }
        }

        const ticket_uuid = this.props && this.props.ticket_uuid;
        await getBackend2().ticketing.moveToPrevStep({
            uuid: ticket_uuid,
            field_values: this.state.field_values,
        });

        // return to inbox
        this.setState({
            successfull_save: true,
            error: "",
        });

        if (this.props.reduxDispatch !== undefined) {
            // switch to inbox view
            setTimeout(() => {
                this.props.reduxDispatch && this.props.reduxDispatch({ type: SET_INBOX_VIEW, data: undefined });
            }, 2000);
        } else {
            if (this.props.close !== undefined) {
                // wait 2 seconds and then close the modal
                setTimeout(() => {
                    this.props.close && this.props.close();
                }, 2000);
            }
        }
    }

    getUpdatedFieldValues(name: string, value: string): tt.IFieldValuePair[] {
        const field_values = this.state.field_values || [];
        const index = field_values.findIndex(fv => fv.name === name);
        if (index >= 0) {
            const field_value = field_values[index];
            field_value.value = value;
            field_values[index] = field_value;
        } else {
            field_values.push({
                name: name,
                value: value,
            });
        }
        return [...field_values];
    }

    onChange = (field_name: string, value: string | boolean, alsoSave = false): void => {
        const field_values = this.getUpdatedFieldValues(field_name, String(value));
        if (alsoSave) {
            this.setState({ field_values }, () => this.onSave());
        } else {
            this.setState({ field_values });
        }
    };

    renderSuccessfullSave() {
        if (this.state.error) {
            return <ErrorComponent key={"error"} msg={this.state.error} type="error" />;
        }
        if (!this.state.successfull_save) {
            return null;
        }
        const msg =
            translate("common.success_save", "Data saved successfuly") +
            ". " +
            translate("common.redirected_shortly", "You will be redirected shortly.,,,");
        return <ErrorComponent key={"success"} msg={msg} type="success" />;
    }

    getUserStepsFields() {
        const ticket = this.state.ticket;

        if (!ticket || !ticket.user_step) {
            if (this.state.text_to_show !== "") {
                return <div>{this.state.text_to_show}</div>;
            } else {
                return (
                    <div>
                        <FormattedMessage id="Ticketing.no_need" />
                    </div>
                );
            }
        }

        const user_steps: React.ReactNode[] = [];
        for (let i = 0; i < ticket.user_step.fields.length; i++) {
            const user_step_field = ticket.user_step.fields[i];

            const field_value_pair = this.state.field_values.find(fv => fv.name === user_step_field.name);
            let field_value = "";

            if (field_value_pair) {
                field_value = field_value_pair.value;
            } else {
                field_value = user_step_field.def_value ?? "";
            }

            user_steps.push(
                <UserStepField
                    key={i}
                    field={user_step_field}
                    type={user_step_field.type}
                    value={field_value || ""}
                    onChange={this.onChange}
                    onSave={() => this.onSave()}
                    is_kiosk={this.props.is_kiosk}
                />
            );
        }
        return user_steps;
    }

    renderNextButtonIfNeeded() {
        if (!this.state.ticket || !this.state.ticket.user_step) {
            return null;
        }
        const user_step = this.state.ticket.user_step;
        const skip_render = user_step.fields.some(x => x.type == "buttons");
        if (skip_render) {
            return null;
        }

        const button_position = this.props.is_kiosk
            ? "ticket-field pt-4 user-step-buttons-centered"
            : "ticket-field pt-4 user-step-buttons";

        const button_style = this.props.is_kiosk
            ? "btn btn-primary user-step-field-button-kiosk"
            : "btn btn-primary user-step-field-button";
        const button2_style = this.props.is_kiosk ? "btn user-step-field-button-kiosk" : "btn user-step-field-button";

        let return_button_title = translate("common.move_to_prev_step", "Move to prev step");
        if (user_step && user_step.return_button_title) {
            return_button_title = user_step.return_button_title;
        }

        return (
            <div className={button_position}>
                <button
                    className={button_style}
                    style={{ marginBottom: "10px", marginTop: "10px" }}
                    onClick={() => this.onSave()}
                >
                    {user_step.button_title || translate("common.save", "Save")}
                </button>
                <button
                    className={button2_style}
                    style={{ marginBottom: "10px", marginTop: "10px" }}
                    onClick={() => this.onSave(true)}
                >
                    {translate("common.save_data_only", "Save data only")}
                </button>
                {
                    // show move to prev step button if there is a prev step
                    this.state.ticket_user_step.has_prev_step && (
                        <button
                            className={button2_style}
                            style={{ marginBottom: "10px", marginTop: "10px" }}
                            onClick={() => this.rejectAndMoveToPrevStep()}
                        >
                            {return_button_title}
                        </button>
                    )
                }
            </div>
        );
    }

    renderComments() {
        // TODO: this code is specific to Kolektor tool_setup process, it should be refactored to be more generic,
        // the displayed fields/comments should be defined by the user_step in the workflow
        if (!this.state.ticket.user_step || this.state.ticket_variables.length < 1) {
            return null;
        }

        const comments = this.state.ticket_variables.filter(el => el.name.endsWith("_comment") && el.value != "");

        if (comments.length === 0) {
            return null;
        }

        return (
            <>
                <div>{translate("common.comment", "Comment")}:</div>
                {comments.map(el => {
                    return (
                        <div key={el.name}>
                            <b>{comment_translations[el.name]}:</b> {el.value}
                        </div>
                    );
                })}
            </>
        );
    }

    renderBackButton() {
        if (!this.state.ticket || !this.state.ticket.user_step || !this.props.reduxDispatch) {
            return null;
        }

        const style_buttons = this.props.is_kiosk ? "btn user-step-field-button-kiosk" : "btn user-step-field-button";
        const button_position = this.props.is_kiosk
            ? "ticket-field user-step-buttons-centered"
            : "ticket-field user-step-buttons";

        return (
            <div className={button_position}>
                <button
                    className={style_buttons}
                    onClick={() =>
                        this.props.reduxDispatch && this.props.reduxDispatch({ type: SET_INBOX_VIEW, data: undefined })
                    }
                >
                    <FormattedMessage id="common.back" defaultMessage="back" />
                </button>
            </div>
        );
    }

    getUserStepTitle() {
        const title = (this.state.ticket && this.state.ticket.user_step && this.state.ticket.user_step.title) || "";

        return <div className={this.props.is_kiosk ? "user-step-title-big" : "user-step-title-small"}>{title}</div>;
    }

    getUserStepText() {
        const text = (this.state.ticket && this.state.ticket.user_step && this.state.ticket.user_step.text) || "";

        return (
            <div className={this.props.is_kiosk ? "user-step-subtitle-big" : "user-step-subtitle-small"}>{text}</div>
        );
    }

    onOpenModal(): void {
        this.setState({ show_modal: true });
    }

    onCloseModal(): void {
        this.setState({ show_modal: false });
    }

    renderModal() {
        return (
            <TicketDetailsModal
                show_ticket_details_uuid={this.state.show_modal ? this.props.ticket_uuid : null}
                onClose={() => this.onCloseModal()}
                requestRefreshCallback={() => null}
            />
        );
    }

    renderAttachments() {
        // TODO - this code should be refactored to be more generic, the displayed attachments (if any)
        // and their order should be defined by the user_step in the workflow

        const attachments_to_render = this.state.ticket_attachments.filter(
            attachment => attachment.type === "markdown"
        );

        if (attachments_to_render.length === 0) {
            return null;
        }

        return attachments_to_render.map<React.ReactNode>(attachment => {
            if (attachment.type === "markdown") {
                return (
                    <div key={attachment.uuid}>
                        <div> [{attachment.title}] </div>
                        <SimpleMarkdownRenderer markdown={attachment.content} />
                    </div>
                );
            } else {
                return null;
            }
        });
    }

    render() {
        const ticket = this.state.ticket;
        if (ticket.user_step === undefined) {
            return null;
        }

        return (
            <div>
                <div className="ticket">
                    {this.getUserStepTitle()}
                    <div>
                        {!this.props.is_kiosk && this.state.ticket && this.state.ticket.user_step !== null && (
                            <div className="pull-right p-3 d-none d-md-block">
                                <SpanButton
                                    onClick={() => this.onOpenModal()}
                                    is_active={false}
                                    toggle_active_status={false}
                                    title={translate("common.details", "Details")}
                                    tooltip={translate(
                                        "ToolSetup.ticket_details_tooltip",
                                        "Show details of this ticket (history, data, etc)"
                                    )}
                                    value={""}
                                ></SpanButton>

                                {this.state.show_modal}
                            </div>
                        )}
                        {this.getUserStepText()}
                    </div>
                    <div>
                        <div style={{ paddingTop: "30px" }}>{this.getUserStepsFields()}</div>
                        <div style={{ paddingTop: "10px" }}>{this.renderComments()}</div>
                        {this.renderNextButtonIfNeeded()}
                        {this.renderSuccessfullSave()}
                        {this.renderModal()}
                    </div>
                </div>
                <div className="ticket">{this.renderAttachments()}</div>
                {this.renderBackButton()}
            </div>
        );
    }
}
export default UserStep;
